import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { ApiError } from '@/lib/api'
import { formatDate, formatMoney, formatNumber, formatTotals } from '@/lib/format'
import {
  fetchBoard,
  fetchColumnCards,
  fetchFunnels,
  isLosingStage,
  moveDealToStage,
  type Board,
  type BoardCard,
  type BoardColumn,
  type Funnel,
} from './dealApi'
import { CloseDealForm } from './CloseDealForm'
import { ForecastChip, noForecastText } from './ForecastChip'
import type { Locale } from '@/i18n'
import type { FilterSpec } from '@/lists/types'
import { Icon } from '@/components/Icon'
import { StateBox } from '@/lists/StateBox'
import { localToday } from '@/lists/quickFilters'
import '@/styles/lists.css'

/**
 * Канбан-доска сделок (тикет #71, ТЗ §4.2).
 *
 * ## Что здесь принципиально
 *
 * **Перенос мышью зовёт доменный метод, а не правит стадию.** Сервер по этому вызову
 * пишет строку истории, пересчитывает маржу на выигрышной стадии, публикует
 * `DealWon`/`DealLost` и запускает правила автоматизации после коммита. Своё «оптимистичное»
 * обновление стадии на клиенте дало бы доску, где карточка двигается, а всё перечисленное
 * молчит — и обнаружилось бы это на отчёте о конверсии через квартал.
 *
 * **Поэтому карточка возвращается на место, если сервер отказал.** Оптимистичный перенос
 * без отката показывает сделку в стадии, в которой её нет: следующий, кто откроет доску,
 * увидит другое, и объяснить расхождение будет нечем.
 *
 * **Подсветку застоя считает сервер.** `stuck` приходит с ним, потому что тот же признак
 * даёт отчёт о застрявших сделках (`findStuck`), а две реализации разошлись бы ровно там,
 * где на подсветку смотрят.
 *
 * ## Под объёмом
 *
 * Замер на стенде: 1108 открытых сделок — ~20 000 DOM-узлов и по `<select>` на карточку,
 * вкладка переставала отвечать. Поэтому колонка показывает первую порцию карточек и
 * кнопку «Показать ещё», а **шапка колонки берёт счётчик и суммы с сервера** — по всей
 * колонке, а не по пришедшим карточкам: «сделок: 30» над колонкой из восьмисот — ложь о
 * воронке. Переключатель стадии — одно меню на экран по кнопке, а не тысяча select-ов.
 *
 * ## Про HTML5 drag-and-drop
 *
 * Взят родной браузерный, а не библиотека: доска — это список колонок с карточками, и
 * `dragstart`/`dragover`/`drop` покрывают ровно её. Библиотека сюда пришла бы вместе с
 * сенсорами, виртуализацией и собственной моделью состояния, а платить за них нечем.
 *
 * **Клавиатурная альтернатива обязательна.** Перетаскивание мышью недоступно с клавиатуры
 * и с тачпада без нажатия, поэтому у каждой карточки есть кнопка с меню стадий. Доска,
 * которой нельзя пользоваться без мыши, — это раздел, закрытый для части сотрудников, и
 * закрытый молча.
 */

interface DealBoardProps {
  /**
   * Фильтры списка, применённые к доске.
   *
   * Приходят снаружи, а не заводятся здесь: переключение доска/таблица не должно терять
   * фильтр — иначе его набирают заново каждый раз, и переключаться перестают.
   */
  filters: FilterSpec[]
  /** Ресурс RBAC: доска — тот же `sales.deal`, но право проверяется явно. */
  resource: string
}

/** Догруженные порции колонок, привязанные к версии доски, на которую они догружались. */
interface Extra {
  /** `dataUpdatedAt` доски: перечитанная доска начинает колонки заново. */
  at: number
  byStage: Record<string, { cards: BoardCard[]; nextCursor: string | null }>
}

export function DealBoard({ filters, resource }: DealBoardProps) {
  const { t, user, locale } = useApp()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [funnelId, setFunnelId] = useState<string | null>(null)
  const [dragging, setDragging] = useState<string | null>(null)
  const [hoverStage, setHoverStage] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  /** Карточка, у которой открыто меню переноса. Одно меню на экран. */
  const [menuFor, setMenuFor] = useState<string | null>(null)
  const [extra, setExtra] = useState<Extra>({ at: -1, byStage: {} })
  const [loadingStage, setLoadingStage] = useState<string | null>(null)

  const funnelsQuery = useQuery({
    queryKey: ['deal-funnels'],
    queryFn: ({ signal }) => fetchFunnels(signal),
    // Воронки — конфигурация: меняются администратором, а не в течение сессии.
    staleTime: 30 * 60 * 1000,
    retry: false,
  })

  const funnels = useMemo(() => funnelsQuery.data ?? [], [funnelsQuery.data])
  // Воронка по умолчанию — помеченная `is_default`, а не первая по алфавиту: новая
  // сделка попадает именно в неё, и доска обязана открываться там же, где идёт работа.
  const activeFunnelId =
    funnelId ?? funnels.find((funnel) => funnel.defaultFunnel)?.id ?? funnels[0]?.id ?? null

  const boardQuery = useQuery({
    queryKey: ['deal-board', activeFunnelId],
    enabled: Boolean(activeFunnelId),
    queryFn: ({ signal }) => fetchBoard(activeFunnelId as string, signal),
    retry: false,
  })

  const mayMove = can(user, resource, 'update')

  const moveMutation = useMutation({
    mutationFn: ({ dealId, stageCode, reasonId, note }: MoveArgs) =>
      moveDealToStage(dealId, stageCode, reasonId, note),
    onSuccess: () => {
      setNotice(null)
      setClosing(null)
      // Перечитываем доску целиком, а не правим карточку на месте: сервер мог поменять
      // не только стадию — вероятность, статус, срок закрытия и подытоги ОБЕИХ колонок
      // меняются тем же переходом, и собранное на клиенте состояние разошлось бы с ними.
      // Догруженные порции при этом сбрасываются (они привязаны к версии доски): после
      // переноса порядок колонки другой, и старый курсор показал бы его неверно.
      void queryClient.invalidateQueries({ queryKey: ['deal-board', activeFunnelId] })
      // Список сделок тоже устарел: та же сделка в нём показывает старую стадию.
      void queryClient.invalidateQueries({ queryKey: ['list', 'sales.deal'] })
    },
    onError: (error) => {
      // Отказ обязан быть виден: карточка вернулась на место (доска не перерисовывалась
      // оптимистично), и без сообщения это выглядит как «перетаскивание не работает».
      setNotice(error instanceof ApiError ? error.message : String(error))
    },
  })

  /** Перенос в терминальную стадию, ждущий причины (#100). */
  const [closing, setClosing] = useState<{ card: BoardCard; column: BoardColumn } | null>(null)

  /**
   * Перенос карточки в стадию.
   *
   * В терминальную стадию — через форму причины, **до** отправки: сервер без причины
   * проигрыша всё равно откажет, но отказ после перетаскивания выглядит поломкой —
   * карточка прыгнула назад, а почему, написано в стороне. Отмена формы — отмена переноса,
   * а не перенос без причины: сделка, проигранная «без причины», выпадает из отчёта об
   * отказах (ТЗ §4.6), ради которого причину и спрашивают.
   */
  const moveTo = useCallback(
    (card: BoardCard, column: BoardColumn) => {
      setMenuFor(null)
      if (card.stageId === column.stageId) return
      if (isLosingStage(column) || column.stageType === 'won') {
        setClosing({ card, column })
        return
      }
      moveMutation.mutate({ dealId: card.id, stageCode: column.stageCode, reasonId: null, note: null })
    },
    [moveMutation],
  )

  const board = boardQuery.data
  // Порции, догруженные к другой версии доски, не показываются: их курсоры описывают
  // колонку, которой уже нет.
  const loaded = extra.at === boardQuery.dataUpdatedAt ? extra.byStage : {}

  /** Догрузка следующей порции колонки по курсору сервера. */
  const loadMore = async (column: BoardColumn) => {
    if (!board || !activeFunnelId) return
    const current = loaded[column.stageId]
    const cursor = current ? current.nextCursor : column.nextCursor
    if (!cursor) return
    const at = boardQuery.dataUpdatedAt
    setLoadingStage(column.stageId)
    try {
      const page = await fetchColumnCards(activeFunnelId, column.stageId, cursor)
      setExtra((previous) => {
        const base = previous.at === at ? previous.byStage : {}
        const before = base[column.stageId]?.cards ?? []
        return {
          at,
          byStage: {
            ...base,
            [column.stageId]: { cards: [...before, ...page.cards], nextCursor: page.nextCursor ?? null },
          },
        }
      })
    } catch (error) {
      setNotice(error instanceof ApiError ? error.message : String(error))
    } finally {
      setLoadingStage(null)
    }
  }

  // ------------------------------------------------------------------ отрисовка

  if (funnelsQuery.isLoading) return <BoardSkeleton label={t('app.loading')} />
  if (funnelsQuery.error) {
    const forbidden = funnelsQuery.error instanceof ApiError && funnelsQuery.error.isForbidden
    return forbidden ? (
      <StateBox kind="forbidden" title={t('access.denied')} description={t('access.deniedHint')} />
    ) : (
      <StateBox
        kind="error"
        title={t('app.error')}
        actions={
          <button type="button" className="btn btn-primary btn-sm" onClick={() => void funnelsQuery.refetch()}>
            <Icon name="refresh" size="sm" />
            {t('list.retry')}
          </button>
        }
      />
    )
  }
  if (funnels.length === 0) {
    // Активных воронок нет — это настройка, а не пустая доска: пустой экран читается как
    // «сделок нет», и его начинают чинить не там.
    return <StateBox kind="setup" title={t('board.noFunnels')} description={t('board.noFunnelsHint')} />
  }

  /** Все карточки колонки на экране: первая порция и догруженные. */
  const cardsOf = (column: BoardColumn): BoardCard[] => [
    ...column.cards,
    ...(loaded[column.stageId]?.cards ?? []),
  ]

  // Терминальные стадии — не колонки, а зоны «Выиграна / Проиграна» под доской (дизайн):
  // открытых сделок в них нет по построению (сервер отдаёт их пустыми), а места на экране
  // они занимали бы столько же, сколько рабочие стадии.
  const columns = board?.columns ?? []
  const openColumns = columns.filter((column) => !isTerminal(column))
  const terminalColumns = columns.filter(isTerminal)
  const draggedCard = dragging
    ? columns.flatMap((column) => cardsOf(column)).find((card) => card.id === dragging)
    : undefined
  const today = localToday()

  const handlers = (column: BoardColumn) => ({
    onDragOver: (event: React.DragEvent) => {
      // preventDefault обязателен: без него браузер считает область непринимающей, и drop
      // не наступает вовсе — доска молча не работает.
      if (!mayMove) return
      event.preventDefault()
      setHoverStage(column.stageId)
    },
    onDragLeave: () => setHoverStage(null),
    onDrop: (event: React.DragEvent) => {
      event.preventDefault()
      setHoverStage(null)
      const dealId = event.dataTransfer?.getData('text/plain') || dragging
      const card = columns
        .flatMap((candidate) => cardsOf(candidate))
        .find((candidate) => candidate.id === dealId)
      setDragging(null)
      if (card) moveTo(card, column)
    },
  })

  /** Соседняя открытая стадия — для Shift+стрелки с клавиатуры. */
  const neighbour = (card: BoardCard, step: 1 | -1): BoardColumn | undefined => {
    const index = openColumns.findIndex((column) => column.stageId === card.stageId)
    return index < 0 ? undefined : openColumns[index + step]
  }

  const openCount = openColumns.reduce((sum, column) => sum + column.count, 0)

  return (
    <section className="deal-board">
      <div className="toolbar">
        <label className="row t-sm secondary">
          {t('board.funnel')}
          <select
            className="input input-sm"
            value={activeFunnelId ?? ''}
            onChange={(event) => setFunnelId(event.target.value)}
          >
            {funnels.map((funnel: Funnel) => (
              <option key={funnel.id} value={funnel.id}>
                {funnel.name}
              </option>
            ))}
          </select>
        </label>
        <span className="spacer" />
        {board && (
          <span className="t-sm secondary" data-testid="board-weighted">
            {t('board.summary', {
              count: formatNumber(openCount, locale),
              amount: formatTotals(sumTotals(openColumns, 'amount'), locale),
              weighted: formatTotals(board.weightedTotals ?? [], locale),
            })}
          </span>
        )}
        {/* Прогноз воронки — второй цифрой рядом с взвешенной по стадиям (#128). Цифра —
            только когда прогноз есть у каждой сделки: сумма по части воронки выдавала бы
            себя за всю. Иначе — сколько сделок с прогнозом или почему его нет. */}
        {board && (
          <span className="t-sm secondary" data-testid="board-forecast">
            {forecastSummary(board, openCount, locale, t)}
          </span>
        )}
      </div>

      {closing && (
        <div
          className="scrim list-scrim"
          onKeyDown={(event) => {
            if (event.key === 'Escape') setClosing(null)
          }}
        >
          <div className="modal board-close">
            <CloseDealForm
              kind={isLosingStage(closing.column) ? 'lost' : 'won'}
              title={closing.card.title}
              pending={moveMutation.isPending}
              onCancel={() => setClosing(null)}
              onConfirm={(reasonId, note) =>
                moveMutation.mutate({
                  dealId: closing.card.id,
                  stageCode: closing.column.stageCode,
                  reasonId,
                  note,
                })
              }
            />
          </div>
        </div>
      )}

      {/* Отказ сервера при переносе — словами и с выходом: карточка осталась на месте
          (доска не перерисовывалась оптимистично), и без сообщения это выглядело бы как
          «перетаскивание не работает». */}
      {notice && (
        <div className="alert alert-danger" role="alert">
          <Icon name="alert" />
          <span>{notice}</span>
          <span className="spacer" />
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => {
              setNotice(null)
              void boardQuery.refetch()
            }}
          >
            <Icon name="refresh" size="sm" />
            {t('board.refresh')}
          </button>
        </div>
      )}

      {boardQuery.isLoading && <BoardSkeleton label={t('app.loading')} />}
      {boardQuery.error &&
        (boardQuery.error instanceof ApiError && boardQuery.error.isForbidden ? (
          <StateBox kind="forbidden" title={t('access.denied')} description={t('access.deniedHint')} />
        ) : (
          <StateBox
            kind="error"
            title={t('app.error')}
            actions={
              <button type="button" className="btn btn-primary btn-sm" onClick={() => void boardQuery.refetch()}>
                <Icon name="refresh" size="sm" />
                {t('board.refresh')}
              </button>
            }
          />
        ))}

      {board && openCount === 0 && (
        <StateBox kind="empty" title={t('board.empty')} description={t('board.emptyHint')} />
      )}

      {board && openCount > 0 && (
        <div className="board" aria-label={t('board.label')}>
          {openColumns.map((column, columnIndex) => {
            const shown = cardsOf(column)
            const cards = visibleCards(shown, filters)
            const extraPart = loaded[column.stageId]
            const nextCursor = extraPart ? extraPart.nextCursor : (column.nextCursor ?? null)
            // Остаток считается от серверного счётчика колонки, а не от длины списка:
            // список — это то, что успело приехать, а не то, что есть.
            const remaining = Math.max(0, column.count - shown.length)
            const isTarget =
              hoverStage === column.stageId &&
              draggedCard !== undefined &&
              draggedCard.stageId !== column.stageId
            return (
              <section
                key={column.stageId}
                className={`col${isTarget ? ' is-target' : ''}`}
                aria-label={t('board.column', { stage: column.stageName })}
                data-testid={`column-${column.stageCode}`}
                {...handlers(column)}
              >
                {/* Счётчик и суммы — с сервера, по всей колонке в области видимости.
                    Сложение пришедших карточек дало бы «30» над колонкой из восьмисот. */}
                <div className="col-h" data-testid={`totals-${column.stageCode}`}>
                  <div className="t">
                    <span className="stage">
                      {/* Стадия — позиция в воронке, а не цвет (дизайн). */}
                      <span className="pips" aria-hidden="true">
                        {openColumns.map((other, index) => (
                          <i key={other.stageId} className={index <= columnIndex ? 'on' : undefined} />
                        ))}
                      </span>
                      <h2 className="col-title">{column.stageName}</h2>
                    </span>
                    <span className="spacer" />
                    <span
                      className="count-pill"
                      data-testid={`count-${column.stageCode}`}
                      title={t('board.columnCount', { count: formatNumber(column.count, locale) })}
                    >
                      {formatNumber(column.count, locale)}
                    </span>
                  </div>
                  <div className="s">
                    <span className="strong col-sum">
                      {formatTotals(
                        (column.totals ?? []).map((total) => ({
                          currency: total.currency,
                          amount: total.amount,
                        })),
                        locale,
                      )}
                    </span>
                    <span title={t('board.weightedHint', { probability: column.probability })}>
                      {t('board.weightedShort', {
                        amount: formatTotals(
                          (column.totals ?? []).map((total) => ({
                            currency: total.currency,
                            amount: total.weightedAmount,
                          })),
                          locale,
                        ),
                      })}
                    </span>
                    {column.count > 0 && (
                      <span
                        data-testid={`forecast-${column.stageCode}`}
                        title={t('board.forecastHint')}
                      >
                        {columnForecast(column, locale, t)}
                      </span>
                    )}
                  </div>
                  <div className="t-xs muted">
                    {column.maxDaysInStage !== null
                      ? t('board.columnMeta', {
                          probability: column.probability,
                          limit: column.maxDaysInStage,
                        })
                      : t('board.columnProbability', { probability: column.probability })}
                  </div>
                </div>

                {isTarget && (
                  <div className="drop-slot">{t('board.dropHere', { stage: column.stageName })}</div>
                )}

                <ul className="col-cards">
                  {cards.map((card) => {
                    const overdue = card.expectedCloseOn !== null && card.expectedCloseOn < today
                    const left = neighbour(card, -1)
                    const right = neighbour(card, 1)
                    return (
                      <li
                        key={card.id}
                        // Признак застоя — на карточке словами, а не в подсказке: он
                        // существует, чтобы его увидели, не наводя мышь.
                        className={[
                          'kcard',
                          card.stuck ? 'board-card-stuck' : '',
                          dragging === card.id ? 'is-ghost' : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        data-testid={`card-${card.number}`}
                        draggable={mayMove}
                        tabIndex={0}
                        onDragStart={(event) => {
                          setDragging(card.id)
                          event.dataTransfer?.setData('text/plain', card.id)
                        }}
                        onDragEnd={() => {
                          setDragging(null)
                          setHoverStage(null)
                        }}
                        onKeyDown={(event) => {
                          // Shift+← / Shift+→ — в соседнюю стадию (дизайн: клавиатура).
                          if (!mayMove || !event.shiftKey || event.target !== event.currentTarget) return
                          const target =
                            event.key === 'ArrowRight' ? right : event.key === 'ArrowLeft' ? left : undefined
                          if (target) {
                            event.preventDefault()
                            moveTo(card, target)
                          }
                        }}
                      >
                        <div className="kcard-head">
                          <button
                            type="button"
                            className="ttl"
                            onClick={() => navigate(`/deals/${card.id}`)}
                          >
                            {card.title}
                          </button>
                          {/* Клавиатурная альтернатива перетаскиванию — кнопка с меню
                              стадий. Одна кнопка на карточку и одно меню на экран: select
                              на каждой карточке давал тысячу списков стадий в DOM. */}
                          {mayMove && (
                            <button
                              type="button"
                              className="btn btn-ghost btn-sm btn-icon kcard-move"
                              aria-label={t('board.moveTo', { deal: card.number })}
                              aria-haspopup="menu"
                              aria-expanded={menuFor === card.id}
                              onClick={() => setMenuFor(menuFor === card.id ? null : card.id)}
                            >
                              <Icon name="more" size="sm" className="i-dots" />
                            </button>
                          )}
                        </div>
                        {mayMove && menuFor === card.id && (
                          <MoveMenu
                            label={t('board.moveTo', { deal: card.number })}
                            columns={columns.filter((candidate) => candidate.stageId !== card.stageId)}
                            onPick={(target) => moveTo(card, target)}
                            onClose={() => setMenuFor(null)}
                          />
                        )}
                        <span className="client ellipsis">
                          <span className="mono">{card.number}</span>
                          {card.counterpartyName && <> · {card.counterpartyName}</>}
                        </span>
                        <div className="foot">
                          <span className="strong" style={{ color: 'var(--fg)' }}>
                            {formatMoney(card.amount, card.currency, locale)}
                          </span>
                          <span className="spacer" />
                          {card.expectedCloseOn &&
                            (overdue ? (
                              <span className="flag flag-danger">
                                <Icon name="clock" />
                                {formatDate(card.expectedCloseOn, locale)}
                              </span>
                            ) : (
                              <span>
                                {t('board.expectedClose', {
                                  date: formatDate(card.expectedCloseOn, locale),
                                })}
                              </span>
                            ))}
                          {/* Ответственный — только «мой»: имени сотрудника доска не отдаёт,
                              а инициалы, выдуманные из идентификатора, были бы неправдой. */}
                          {user?.employeeId && card.responsibleEmployeeId === user.employeeId && (
                            <span className="ava ava-me" title={t('board.mine')}>
                              {t('board.mineShort')}
                            </span>
                          )}
                        </div>
                        {/* Статическая вероятность стадии и прогноз модели — рядом, а не одно
                            вместо другого (#128); «почему» — в подсказке прогноза. */}
                        <div className="kcard-prob t-xs secondary" data-testid={`prob-${card.number}`}>
                          <span>{t('board.cardProbability', { probability: card.probability })}</span>
                          <span aria-hidden="true"> · </span>
                          <ForecastChip forecast={card.forecast} status={board?.forecastStatus} />
                        </div>
                        {/* Проблемы — словами с цифрами, а не цветом рамки: цвет не переживает
                            печать и дальтонизм, а «22 из 14 дней» отвечает на вопрос «насколько». */}
                        {(card.stuck || overdue) && (
                          <div className="issue">
                            {card.stuck && (
                              <span className="flag flag-warning">
                                <Icon name="alert" />
                                {t('board.stuck', {
                                  days: card.daysInStage,
                                  limit: card.maxDaysInStage ?? 0,
                                })}
                              </span>
                            )}
                            {overdue && (
                              <span className="flag flag-danger">
                                <Icon name="clock" />
                                {t('board.overdue')}
                              </span>
                            )}
                          </div>
                        )}
                      </li>
                    )
                  })}
                </ul>

                {nextCursor && remaining > 0 && (
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm board-more"
                    disabled={loadingStage === column.stageId}
                    onClick={() => void loadMore(column)}
                  >
                    {t('board.showMore', { count: formatNumber(remaining, locale) })}
                  </button>
                )}
              </section>
            )
          })}
        </div>
      )}

      {/* Зоны закрытия появляются при перетаскивании (дизайн). Проигрыш спросит причину —
          форма открывается до отправки, а не отказом сервера после неё. */}
      {board && terminalColumns.length > 0 && (
        <div className="drop-zones" hidden={!dragging} aria-label={t('board.dropZones')}>
          {terminalColumns.map((column) => (
            <div
              key={column.stageId}
              className={[
                'drop-zone',
                column.stageType === 'won' ? 'won' : '',
                hoverStage === column.stageId ? 'is-over' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              data-testid={`column-${column.stageCode}`}
              {...handlers(column)}
            >
              <Icon name={column.stageType === 'won' ? 'check' : 'close'} />
              {column.stageType === 'won'
                ? t('board.dropWon', { stage: column.stageName })
                : t('board.dropLost', { stage: column.stageName })}
            </div>
          ))}
        </div>
      )}
    </section>
  )
}

/** Скелет доски на время загрузки — пять колонок по три карточки. */
function BoardSkeleton({ label }: { label: string }) {
  return (
    <div className="board" aria-busy="true" aria-label={label} role="status">
      {[0, 1, 2, 3, 4].map((column) => (
        <div key={column} className="col">
          <div className="col-h" style={{ gap: 8 }}>
            <span className="skel" style={{ width: '60%', height: 14 }} />
            <span className="skel" style={{ width: '80%', height: 8 }} />
          </div>
          {[70, 55, 80].map((width) => (
            <div key={width} className="kcard" style={{ gap: 8, cursor: 'default' }}>
              <span className="skel" style={{ width: `${width}%` }} />
              <span className="skel" style={{ width: '50%', height: 8 }} />
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}

/** Терминальная стадия — выигрыш или проигрыш. */
function isTerminal(column: BoardColumn): boolean {
  return column.stageType === 'won' || isLosingStage(column)
}

/** Сумма колонок по валютам — сложить рубли с евро значит выдумать курс. */
function sumTotals(columns: BoardColumn[], field: 'amount' | 'weightedAmount') {
  const byCurrency = new Map<string, number>()
  columns.forEach((column) =>
    (column.totals ?? []).forEach((total) =>
      byCurrency.set(total.currency, (byCurrency.get(total.currency) ?? 0) + total[field]),
    ),
  )
  return [...byCurrency].map(([currency, amount]) => ({ currency, amount }))
}

type Translate = (key: string, params?: Record<string, string | number>) => string

/**
 * Итог воронки по прогнозу (#128).
 *
 * Цифра — только когда прогноз есть у каждой открытой сделки: иначе сумма по части воронки
 * встала бы рядом со взвешенной по всей и читалась бы как сравнимая с ней.
 */
export function forecastSummary(board: Board, openCount: number, locale: Locale, t: Translate): string {
  if (board.forecastStatus !== 'trained') {
    return noForecastText(t, board.forecastStatus)
  }
  const covered = board.forecastCount ?? 0
  if (covered < openCount) {
    return t('board.forecastPartial', {
      covered: formatNumber(covered, locale),
      count: formatNumber(openCount, locale),
    })
  }
  return t('board.forecastSummary', {
    weighted: formatTotals(board.forecastWeightedTotals ?? [], locale),
  })
}

/** Подытог колонки по прогнозу — та же логика «всё или словами», что и у воронки. */
export function columnForecast(column: BoardColumn, locale: Locale, t: Translate): string {
  const covered = column.forecastCount ?? 0
  const complete = (column.totals ?? []).filter((total) => total.forecastWeightedAmount != null)
  if (covered < column.count || complete.length < (column.totals ?? []).length) {
    return t('board.forecastColumnPartial', {
      covered: formatNumber(covered, locale),
      count: formatNumber(column.count, locale),
    })
  }
  return t('board.forecastShort', {
    amount: formatTotals(
      complete.map((total) => ({ currency: total.currency, amount: total.forecastWeightedAmount ?? 0 })),
      locale,
    ),
  })
}

/**
 * Меню стадий для переноса одной карточки.
 *
 * Пункты — кнопки с ролью `menuitem`: Tab и Enter работают без своей обработки клавиш, а
 * Escape закрывает меню. Первый пункт получает фокус при открытии — иначе с клавиатуры
 * до меню, появившегося под кнопкой, не добраться.
 */
function MoveMenu({
  label,
  columns,
  onPick,
  onClose,
}: {
  label: string
  columns: BoardColumn[]
  onPick: (column: BoardColumn) => void
  onClose: () => void
}) {
  return (
    <div
      className="pop kcard-menu"
      role="menu"
      aria-label={label}
      onKeyDown={(event) => {
        if (event.key === 'Escape') onClose()
      }}
    >
      {columns.map((column, index) => (
        <button
          key={column.stageId}
          type="button"
          role="menuitem"
          className="menu-item"
          autoFocus={index === 0}
          onClick={() => onPick(column)}
        >
          {column.stageName}
        </button>
      ))}
    </div>
  )
}

interface MoveArgs {
  dealId: string
  stageCode: string
  reasonId: string | null
  note: string | null
}

/**
 * Применяет фильтры списка к карточкам доски.
 *
 * ## Названо вслух: это НЕ тот же фильтр, что у таблицы
 *
 * Сервер фильтрует список предикатом в SQL по всему каталогу колонок; доска отдаёт
 * готовый набор открытых сделок воронки, и сузить его здесь можно только по тем полям,
 * что приехали на карточке. Поэтому поддержано подмножество — сумма, название,
 * контрагент, ответственный, стадия, срок, — а фильтр по колонке, которой на карточке
 * нет, **не применяется молча**: доска покажет больше строк, чем таблица с тем же
 * фильтром.
 *
 * Это осознанный выбор направления ошибки. Альтернатива — прятать карточки по фильтру,
 * который здесь нечем вычислить, то есть показывать доску, где сделки пропадают без
 * объяснения. Лишняя карточка видна и объяснима, пропавшая — нет.
 *
 * Правильное решение — фильтровать доску на сервере тем же компилятором, что и списки;
 * это требует его переиспользования поверх агрегата доски и в #71 не сделано.
 */
export function visibleCards(cards: BoardCard[], filters: FilterSpec[]): BoardCard[] {
  if (filters.length === 0) return cards
  return cards.filter((card) => filters.every((filter) => matches(card, filter)))
}

/** Значение карточки по имени колонки списка, или `undefined`, если её здесь нет. */
function cardValue(card: BoardCard, column: string): unknown {
  switch (column) {
    case 'id':
      return card.id
    case 'number':
      return card.number
    case 'title':
      return card.title
    case 'amount':
      return card.amount
    case 'currency':
      return card.currency
    case 'status':
      return card.status
    case 'probability':
      return card.probability
    case 'stage_id':
      return card.stageId
    case 'counterparty_id':
      return card.counterpartyId
    case 'responsible_employee_id':
      return card.responsibleEmployeeId
    case 'expected_close_on':
      return card.expectedCloseOn
    default:
      // Колонки нет на карточке — фильтр по ней не применяется. См. Javadoc выше:
      // лишняя карточка объяснима, пропавшая нет.
      return undefined
  }
}

function matches(card: BoardCard, filter: FilterSpec): boolean {
  const value = cardValue(card, filter.column)
  if (value === undefined) return true

  const expected = filter.value
  switch (filter.operator) {
    case 'eq':
      return String(value) === String(expected)
    case 'ne':
      return String(value) !== String(expected)
    case 'contains':
      return String(value ?? '').toLowerCase().includes(String(expected ?? '').toLowerCase())
    case 'starts_with':
      return String(value ?? '').toLowerCase().startsWith(String(expected ?? '').toLowerCase())
    case 'gt':
      return compare(value, expected) > 0
    case 'gte':
      return compare(value, expected) >= 0
    case 'lt':
      return compare(value, expected) < 0
    case 'lte':
      return compare(value, expected) <= 0
    case 'is_null':
      return value === null
    case 'is_not_null':
      return value !== null
    case 'in':
      return Array.isArray(expected)
        ? expected.some((item) => String(item) === String(value))
        : String(expected ?? '').split(',').some((item) => item.trim() === String(value))
    case 'between': {
      const bounds = Array.isArray(expected) ? expected : []
      return compare(value, bounds[0]) >= 0 && compare(value, bounds[1]) <= 0
    }
    default:
      return true
  }
}

/** Сравнение чисел числами, остального строками: «10» иначе меньше «9». */
function compare(left: unknown, right: unknown): number {
  const leftNumber = Number(left)
  const rightNumber = Number(right)
  if (!Number.isNaN(leftNumber) && !Number.isNaN(rightNumber) && left !== null && right !== null) {
    return leftNumber - rightNumber
  }
  return String(left ?? '').localeCompare(String(right ?? ''))
}
